import { useEffect, useCallback } from "react";
import { useAuth } from "../hooks/useAuth";
import { MagicLinkAuth } from "../components/MagicLinkAuth";
import { Logo } from "../components/Logo";
import type { OrganizationInfo } from "../lib/portal-loaders";
import { safePortalRedirect } from "../customer-portal/utils/knowledgeBaseAccess";

interface CustomerPortalLoginProps {
  organizationInfo?: OrganizationInfo;
  // The page to return to after signing in (e.g. a private knowledge base article someone shared)
  redirect?: string;
}

export function CustomerPortalLogin({
  organizationInfo,
  redirect,
}: CustomerPortalLoginProps) {
  const { isAuthenticated, isCustomerPortal } = useAuth();

  // Determine URL pattern and construct redirect URL
  const getPortalUrl = useCallback((path: string) => {
    const isPathBased = window.location.pathname.startsWith("/org/");
    if (isPathBased && organizationInfo) {
      return `/org/${organizationInfo.subdomain}/portal${path}`;
    }
    return `/portal${path}`;
  }, [organizationInfo]);

  // After signing in: the page they were sent here from, when it's a page of this portal, otherwise My Tickets
  const afterSignIn =
    (organizationInfo && safePortalRedirect(redirect, organizationInfo.subdomain)) || getPortalUrl("/tickets");

  useEffect(() => {
    // Redirect if already authenticated as customer
    if (isAuthenticated && isCustomerPortal && organizationInfo) {
      window.location.href = afterSignIn;
    }
  }, [isAuthenticated, isCustomerPortal, organizationInfo, afterSignIn]);

  if (!organizationInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">
            Loading organization...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-900 px-4 py-8 sm:py-12">
      <div className="mb-4 sm:mb-6">
        <Logo
          className="h-16 sm:h-20 md:h-24 w-auto"
          organizationName={organizationInfo.name}
          branding={organizationInfo.portalSettings.branding}
        />
      </div>

      <div className="w-full max-w-md">
        <div className="text-center mb-4 sm:mb-6">
          {!organizationInfo.portalSettings.branding.logoUrl && (
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-2">
              {organizationInfo.name} Support Portal
            </h1>
          )}
        </div>

        <MagicLinkAuth
          organizationId={organizationInfo.organizationId}
          redirectUrl={afterSignIn}
          onSuccess={() => {
            // Optional: Show additional success message or perform other actions
          }}
        />
      </div>
    </div>
  );
}
